import { File, Image, Trash, Upload } from 'lucide-react'
import React, { useCallback, useEffect } from 'react'
import { DropzoneProps, useDropzone } from 'react-dropzone'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type FileUploadProps = {
  name: string
  label?: string
  mode?: 'update' | 'append'
} & DropzoneProps

export const FileUpload = (props: FileUploadProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'expense.upload.form',
  })

  const { name, mode = 'update' } = props

  const { register, unregister, setValue, watch } = useFormContext()

  const files = watch(name)

  const onDrop = useCallback(
    (droppedFiles: File[]) => {
      let newFiles =
        mode === 'update' ? droppedFiles : [...(files || []), ...droppedFiles]
      if (mode === 'append') {
        newFiles = newFiles.reduce((prev, file) => {
          const fo = Object.entries(file)
          if (
            prev.find((e: File) => {
              const eo = Object.entries(e)
              return eo.every(
                ([key, value], index) =>
                  key === fo[index][0] && value === fo[index][1],
              )
            })
          ) {
            return prev
          } else {
            return [...prev, file]
          }
        }, [])
      }

      setValue(name, newFiles, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      })
    },
    [setValue, name, mode, files],
  )

  const deleteUploadedFile = useCallback(
    (fileName: string) => {
      const newFiles = files?.filter((file: File) => file.name !== fileName)
      setValue(name, newFiles, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      })
    },
    [setValue, name, files],
  )

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    onDrop,
    ...props,
  })

  useEffect(() => {
    register(name)
    return () => {
      unregister(name)
    }
  }, [register, unregister, name])

  return (
    <div>
      <div
        className={`cursor-pointer rounded-lg border border-dashed transition-colors ${isDragAccept ? "border-foreground bg-muted" : "border-input hover:bg-muted"}`}
        {...getRootProps()}
      >
        <input
          //   {...props}
          className="sr-only"
          id={name}
          {...getInputProps()}
        />

        <div className="flex w-full flex-col items-center gap-2 px-6 py-14 text-center">
          {isDragAccept ? (
            <div className="text-sm font-medium">{t('file.drop')}</div>
          ) : (
            <div className="flex items-center flex-col gap-1.5">
              <span className="grid size-10 place-items-center rounded-full border bg-background">
                <Upload className="size-4" />
              </span>
              <div className="text-sm font-medium">{t('file.select')}</div>
              <div className="text-xs text-muted-foreground">PDF, JPG nebo PNG, nejvýš 3 soubory po 3 MB</div>
            </div>
          )}
        </div>
      </div>

      <div>
        {!!files?.length && (
          <div className="flex flex-col gap-2 mt-2">
            {files.map((file: File) => {
              return (
                <div
                  key={file.name}
                  className="flex h-14 w-full flex-row items-center justify-between rounded-lg border px-4"
                >
                  <div className="flex items-center flex-row gap-4 h-full">
                    {file.type === 'application/pdf' ? (
                      <File className="w-4 h-4" />
                    ) : (
                      <Image className="w-4 h-4" />
                    )}
                    <div className="flex flex-col gap-0">
                      <div className="text-[0.85rem] font-medium leading-snug">
                        {file.name.split('.').slice(0, -1).join('.')}
                      </div>
                      <div className="text-xs leading-tight text-muted-foreground tabular">
                        .{file.name.split('.').pop()},{' '}
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <div
                    className="cursor-pointer select-none rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-danger"
                    onClick={() => deleteUploadedFile(file.name)}
                  >
                    <Trash className="w-4 h-4" />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
